import { TextKey, RouteName, FeatureFlag } from '@mews-commander/enums';
import { useFeatureFlag, useLocalizeText } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { canCurrentProfileViewSensitiveReports } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { useGetAccountingConfigurationReceivableTrackingEnabled } from '../../../../payment/queries';
import { AssistantNavigation } from './AssistantNavigation';
import { useNavigationSearchResult } from './useNavigationSearchResult';
import { useNavigationPath } from './useNavigationPath';

export const useFinanceNavigation = (): AssistantNavigation[] => {
    const canViewSensitiveReports = useCommanderSelector<boolean>(canCurrentProfileViewSensitiveReports);
    const isManagerReportEnabled = useFeatureFlag(FeatureFlag.managerReportEnabled);
    const { data: receivableTrackingEnabled = false } = useGetAccountingConfigurationReceivableTrackingEnabled();

    const localizeText = useLocalizeText();

    const parentPath = `${localizeText({ textKey: TextKey.Finance })}${UnicodeEntity.Space}${
        UnicodeEntity.ForwardSlash
    }${UnicodeEntity.Space}`;

    const cashierResult = useNavigationSearchResult(
        RouteName.CashierDefault,
        TextKey.Cashier,
        {},
        useNavigationPath(parentPath, TextKey.Cashier)
    );
    const ledgerReportResult = useNavigationSearchResult(
        RouteName.AccountingLedgerReportIndex,
        TextKey.AccountingLedgerReport,
        {},
        useNavigationPath(parentPath, TextKey.AccountingLedgerReport)
    );
    const paymentReportResult = useNavigationSearchResult(
        RouteName.PaymentReportIndex,
        TextKey.PaymentReport,
        {},
        useNavigationPath(parentPath, TextKey.PaymentReport)
    );
    const accountingReportResult = useNavigationSearchResult(
        RouteName.AccountingReportIndex,
        TextKey.AccountingReport,
        {},
        useNavigationPath(parentPath, TextKey.AccountingReport)
    );
    const billReportResult = useNavigationSearchResult(
        RouteName.BillReportIndex,
        TextKey.BillReport,
        {},
        useNavigationPath(parentPath, TextKey.BillReport)
    );
    const journalReportResult = useNavigationSearchResult(
        RouteName.PostingJournalReportIndex,
        TextKey.PostingJournalReport,
        {},
        useNavigationPath(parentPath, TextKey.PostingJournalReport)
    );
    const managerReportResult = useNavigationSearchResult(
        RouteName.ManagerReportIndex,
        TextKey.ManagerReport,
        {},
        useNavigationPath(parentPath, TextKey.ManagerReport)
    );
    const newManagerResult = useNavigationSearchResult(
        RouteName.NewManagerReport,
        TextKey.NewManagerReport,
        {},
        useNavigationPath(parentPath, TextKey.ManagerReport)
    );
    const creditCardReportResult = useNavigationSearchResult(
        RouteName.CreditCardReportIndex,
        TextKey.CreditCardReport,
        {},
        useNavigationPath(parentPath, TextKey.CreditCardReport)
    );
    const chargebackReportResult = useNavigationSearchResult(
        RouteName.ChargebackReportIndex,
        TextKey.ChargebackReport,
        {},
        useNavigationPath(parentPath, TextKey.ChargebackReport)
    );
    const merchantBalanceResult = useNavigationSearchResult(
        RouteName.MerchantBalanceReportIndex,
        TextKey.MerchantBalance,
        {},
        useNavigationPath(parentPath, TextKey.MerchantBalance)
    );
    const cityTaxReportResult = useNavigationSearchResult(
        RouteName.CityTaxReportIndex,
        TextKey.CityTaxReport,
        {},
        useNavigationPath(parentPath, TextKey.CityTaxReport)
    );
    const cityLedgerReportResult = useNavigationSearchResult(
        RouteName.FinanceCityLedger,
        TextKey.CityLedger,
        {},
        useNavigationPath(parentPath, TextKey.CityLedger)
    );
    const payoutReportResult = useNavigationSearchResult(
        RouteName.PayoutReportIndex,
        TextKey.PayoutReport,
        {},
        useNavigationPath(parentPath, TextKey.PayoutReport)
    );

    return [
        cashierResult,
        ledgerReportResult,
        paymentReportResult,
        canViewSensitiveReports ? accountingReportResult : null,
        billReportResult,
        journalReportResult,
        canViewSensitiveReports ? managerReportResult : null,
        creditCardReportResult,
        chargebackReportResult,
        canViewSensitiveReports ? merchantBalanceResult : null,
        cityTaxReportResult,
        receivableTrackingEnabled && canViewSensitiveReports ? cityLedgerReportResult : null,
        canViewSensitiveReports ? payoutReportResult : null,
        isManagerReportEnabled && canViewSensitiveReports ? newManagerResult : null,
    ].filter(Boolean) as AssistantNavigation[];
};
